import type { paths } from '@/constants';
import { isViewPath, validateRedirect } from '@/lib/validateRedirect';

/** The auth pages a learner moves between on the way back to a course. */
export type AuthRoute =
  | typeof paths.login
  | typeof paths.loginSignIn
  | typeof paths.loginSignUp
  | typeof paths.loginVerify
  | typeof paths.resetPassword;

export interface AuthRouteHrefOptions {
  /** Pre-filled identifier for the next page (`auth_as`). */
  authAs?: string | null;
  /**
   * Further query parameters the target page reads (`error`, `reset`,
   * `trace`, `reason`), appended after `redirect` / `auth_as` in insertion
   * order. `undefined` / `null` values are skipped.
   */
  params?: Record<string, string | null | undefined>;
}

/**
 * #1111: the address of an auth page, carrying the current page's sign-in
 * return address (`?redirect=`) and, when given, the pre-filled identifier
 * (`auth_as`) and the page's own parameters (`params`).
 *
 * Every link or navigation between Sign In, Sign Up, Verify and Forgot
 * Password goes through here, so moving between them can never drop the
 * course the learner came from (the switch links used to, and the learner
 * landed on the dashboard). `src/features/accounts/__tests__/authRouteLinks.guard.test.ts`
 * enforces it.
 *
 * The redirect is the current URL's `redirect`, validated the same way every
 * auth page validates it; a missing or hostile value is not carried (the next
 * page then uses its own default, exactly as when it was never there).
 */
export function authRouteHref(
  route: AuthRoute,
  searchParams: URLSearchParams,
  options: AuthRouteHrefOptions = {},
): string {
  const parts: string[] = [];
  const redirect = validateRedirect(searchParams.get('redirect'), { fallback: '' });
  if (redirect) parts.push(`redirect=${encodeURIComponent(redirect)}`);
  if (options.authAs) parts.push(`auth_as=${encodeURIComponent(options.authAs)}`);
  if (options.params) {
    const extra = new URLSearchParams();
    for (const [key, value] of Object.entries(options.params)) {
      if (value !== undefined && value !== null) extra.append(key, value);
    }
    const tail = extra.toString();
    if (tail) parts.push(tail);
  }
  return parts.length > 0 ? `${route}?${parts.join('&')}` : route;
}

/**
 * #1111 (1111b, vingapi#1653): the return address a mailed or texted sign-in
 * link should carry, i.e. the current URL's `redirect` when it is a course
 * (`/view/…`) path, else `undefined` (the field is then omitted from the
 * request). The server accepts only `/view/` paths and silently ignores
 * anything else, so a non-course redirect is never sent.
 *
 * Validation is `validateRedirect` + `isViewPath`, the same pair the landing
 * (`landingPath`) uses, so the link returns exactly where a password sign-in
 * from this page would.
 */
export function linkRequestRedirect(searchParams: URLSearchParams): string | undefined {
  const redirect = validateRedirect(searchParams.get('redirect'), { fallback: '' });
  return redirect && isViewPath(redirect) ? redirect : undefined;
}

/**
 * #1111 (1111b): the sign-in return address for a page the learner is on
 * (`here` = pathname + search). A course page (`/view/…`, validated by
 * `isViewPath`) is carried as `redirect`; any other page carries nothing, so
 * `authRouteHref(route, viewReturnAddress(here))` is exactly `route` there
 * (owner scope is the course return; widening it is a separate decision).
 * Used by the header's Sign In / logo (AppShell) and the 401 bounce
 * (api/client.ts).
 */
export function viewReturnAddress(here: string): URLSearchParams {
  return isViewPath(here) ? returnAddress(here) : new URLSearchParams();
}

/**
 * #1111 (1111b): the sign-in return address for any page (`here` = pathname +
 * search), for the callers that send the learner to sign in from wherever they
 * are: the signed-out route guard (ProtectedRoute), the consume refusal screen
 * (AccessDenied) and the consume save banner (PacketViewer).
 * `authRouteHref(route, returnAddress(here))` validates `here` with
 * `validateRedirect`, exactly as the sign-in page will when it reads it, so a
 * hostile path is never put on the URL.
 */
export function returnAddress(here: string): URLSearchParams {
  return new URLSearchParams({ redirect: here });
}
